/**
 * TodaysPrediction — the main prediction card (TODAYS_PREDICTION).
 *
 * Pure presentational; all content via props, all styling via design tokens
 * (renders inside an `.askk-theme` container).
 *
 * - Top-left radial ambient glow (card-glow stops), behind the content.
 * - Zodiac pill: accentPrimary fill + successGradient border (alpha in stops).
 * - The lucky-color swatch uses an inline background color (data-driven hex
 *   from BE), not a fixed design token.
 */
import { TodaysPredictionData } from '../../api/types'
import { logEvent } from '../../lib/analytics'
import { EventAction, EventComponent } from '../../lib/eventConstants'

interface Props {
  data: TodaysPredictionData
}

// Pseudo-clicks: we capture that the user tapped a lucky attribute (to decide
// whether to build real interactions later) but give NO feedback — no ripple,
// no navigation. Just the analytics event.
function pseudoClick(component: string) {
  logEvent({ section: 'TODAYS_PREDICTION', action: EventAction.CLICK, component })
}

export function TodaysPrediction({ data }: Props) {
  const { date, zodiac, headline, prediction, lucky } = data

  return (
    <div className="relative overflow-hidden rounded-2xl bg-surfaceOneVariant p-3">
      {/* Radial ambient glow at the top-left corner. Smaller radius = fades sooner;
          opacity softens the peak so the corner isn't harsh. */}
      <div className="pointer-events-none absolute -left-[70px] -top-[70px] z-0 h-[140px] w-[140px] opacity-50 [background-image:radial-gradient(circle_closest-side,var(--gradient-card-glow-stops))]" />

      <div className="relative z-10">
        {/* Header: date (gradient text) + zodiac pill */}
        <div className="flex items-center justify-between">
          <span className="bg-clip-text text-bodySmall text-transparent [background-image:linear-gradient(90deg,var(--gradient-text-gradient-secondary-stops))]">
            {date}
          </span>
          {/* Pill: accentPrimary fill (padding box) + successGradient border (border box).
              successGradient stops already carry the 20%→0% orange alpha. */}
          <div className="flex items-center gap-1 rounded-full border border-transparent px-2 py-1 [background-image:linear-gradient(180deg,var(--gradient-accent-primary-stops)),linear-gradient(180deg,var(--gradient-success-stops))] [background-origin:border-box] [background-clip:padding-box,border-box]">
            {zodiac.iconUrl && (
              <img src={zodiac.iconUrl} alt="" className="h-4 w-4 object-cover" />
            )}
            <span className="text-bodySmall text-textPrimary">{zodiac.label}</span>
          </div>
        </div>

        {/* Headline + prediction */}
        <div className="mt-4 flex flex-col gap-2">
          <p className="text-headingXSmall text-textPrimary">{headline}</p>
          <p className="text-bodyMedium text-textSecondary">{prediction}</p>
        </div>

        {/* Lucky attributes */}
        <div className="mt-4 flex flex-col gap-2">
          <div className="flex gap-2">
            {lucky.number && (
              <div
                onClick={() => pseudoClick(EventComponent.LUCKY_NUMBER)}
                className="flex flex-1 flex-col gap-1 rounded-lg border border-textSeparator px-2 py-1.5"
              >
                <span className="text-bodyXSmall text-textSecondary">{lucky.number.label}</span>
                <div className="flex items-center gap-1">
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-surfaceTwo text-labelSmall text-textAccentPrimary">
                    {lucky.number.value}
                  </span>
                  {lucky.number.valueText && (
                    <span className="text-bodyMedium text-textPrimary">{lucky.number.valueText}</span>
                  )}
                </div>
              </div>
            )}
            {lucky.color && (
              <div
                onClick={() => pseudoClick(EventComponent.LUCKY_COLOR)}
                className="flex flex-1 flex-col gap-1 rounded-lg border border-textSeparator px-2 py-1.5"
              >
                <span className="text-bodyXSmall text-textSecondary">{lucky.color.label}</span>
                <div className="flex items-center gap-1">
                  {lucky.color.swatch && (
                    <span
                      className="h-4 w-4 shrink-0 rounded-full"
                      style={{ backgroundColor: lucky.color.swatch }}
                    />
                  )}
                  <span className="text-bodyMedium text-textPrimary">{lucky.color.value}</span>
                </div>
              </div>
            )}
          </div>
          {lucky.time && (
            <div
              onClick={() => pseudoClick(EventComponent.LUCKY_TIME)}
              className="flex items-center justify-between rounded-lg border border-textSeparator px-2 py-1.5"
            >
              <span className="text-bodyXSmall text-textSecondary">{lucky.time.label}</span>
              <span className="text-bodyMedium text-textPrimary">{lucky.time.value}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default TodaysPrediction
